import React, { useEffect, useState, useRef } from 'react';
import ToolCategory from './ToolCategory';
import TabNavigation from './TabNavigation';
import Footer from './Footer';
import PartnerPromotion from './PartnerPromotion';
import { careerCoachingTools, marketingDesignTools, codingAppDevelopmentTools } from '../data/tools';
import { ChevronDownIcon } from 'lucide-react';
const DisclosureRow = ({
  onClick,
  isOpen,
  count
}) => {
  return <button onClick={onClick} className="w-full flex items-center justify-center py-2 text-sm text-link dark:text-dark-link hover:text-link-hover dark:hover:text-dark-link-hover focus:outline-none" data-id="element-58">
      <span data-id="element-59">{isOpen ? 'Show less' : 'Show all'}</span>
      <ChevronDownIcon className={`ml-1 w-4 h-4 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-60" />
    </button>;
};
const MainContent = () => {
  const [activeTab, setActiveTab] = useState('career-coaching');
  const [hasScrolled, setHasScrolled] = useState(false);
  // Create refs for each category
  const careerCoachingRef = useRef(null);
  const marketingDesignRef = useRef(null);
  const codingAppDevRef = useRef(null);
  const tabs = [{
    id: 'career-coaching',
    name: 'Career Coaching'
  }, {
    id: 'marketing-&-design',
    name: 'Marketing & Design'
  }, {
    id: 'coding-app-dev',
    name: 'Coding & App Development'
  }];
  // Handle tab change and scrolling
  const handleTabChange = tabId => {
    setActiveTab(tabId);
    // Only scroll on mobile
    if (window.innerWidth >= 768) return;
    // Scroll back to the main heading "AI tools that work for you"
    requestAnimationFrame(() => {
      const headerHeight = 48; // Fixed header height
      const tabHeight = 66; // Approximate tab navigation height (py-3 + border)
      const scrollPosition = headerHeight + tabHeight; // Scroll to just below both header and tabs
      window.scrollTo({
        top: scrollPosition + 12,
        behavior: 'smooth'
      });
    });
  };
  // Add scroll event listener to detect if user has scrolled
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setHasScrolled(true);
      } else {
        setHasScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (!hash) return;
      let tabId = hash.substring(1);
      // handle mobile
      if (window.innerWidth < 768) {
        // id from LI is marketing-design, need to make this conversion with minimum code change.
        if (['career-coaching', 'marketing-design', 'coding-app-dev'].includes(tabId)) {
          tabId = tabId === 'marketing-design' ? 'marketing-&-design' : tabId;
          setActiveTab(tabId);
        }
      } else {
        const el = document.getElementById(tabId);
        el && el.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    };
    const timer = setTimeout(scrollToHash, 200);
    return () => {
      clearTimeout(timer);
    };
  }, []);
  return <div className="flex-1 flex flex-col h-full md:h-[calc(100vh-60px)]" data-id="element-61">
      {/* Fixed header section */}
      <div className="px-4 pt-4 pb-0 md:p-4" data-id="element-62">
        <h1 className="text-2xl font-medium text-text dark:text-dark-text mb-2 md:text-3xl" data-id="element-63">
          AI tools that work for you
        </h1>
        <p className="text-text-meta dark:text-dark-text-meta" data-id="element-64">
          Discover AI tools from verified companies, recommended by real
          professionals to help you work faster and smarter.
        </p>
      </div>
      {/* Mobile tab navigation - sticky beneath header */}
      <div className={`md:hidden sticky top-[49px] z-20 transition-all duration-200 ${hasScrolled ? 'bg-white dark:bg-dark-background border-b border-gray-200 dark:border-dark-border shadow-sm' : 'bg-transparent'}`} data-id="element-65">
        <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} data-id="element-66" />
      </div>
      {/* Content section */}
      <div className="overflow-y-auto flex-1 px-4 py-4 md:pr-2 pb-16" data-id="element-67">
        {/* Mobile: Show based on active tab */}
        <div className="md:hidden" data-id="element-68">
          {activeTab === 'career-coaching' && <ToolCategory ref={careerCoachingRef} title="Career Coaching" description="From resume drafts to practice interviews, AI tools can help you land your next role faster." tools={careerCoachingTools} isMobile={true} data-id="element-69" />}
          {activeTab === 'marketing-&-design' && <ToolCategory ref={marketingDesignRef} title="Marketing & Design" description="From drafting copy to designing assets, AI tools can help you design and launch faster." tools={marketingDesignTools} isMobile={true} data-id="element-70" />}
          {activeTab === 'coding-app-dev' && <ToolCategory ref={codingAppDevRef} title="Coding & App Development" description="From writing code to troubleshooting, AI helps speed up every stage of the build." tools={codingAppDevelopmentTools} isMobile={true} data-id="element-71" />}
          <PartnerPromotion data-id="element-72" />
          <Footer data-id="element-73" />
        </div>
        {/* Desktop: Show all categories */}
        <div className="hidden md:block" data-id="element-74">
          <ToolCategory title="Career Coaching" description="From resume drafts to practice interviews, AI tools can help you land your next role faster." tools={careerCoachingTools} data-id="element-75" />
          <ToolCategory title="Marketing & Design" description="From drafting copy to designing assets, AI tools can help you design and launch faster." tools={marketingDesignTools} data-id="element-76" />
          <ToolCategory title="Coding & App Development" description="From writing code to troubleshooting, AI helps speed up every stage of the build." tools={codingAppDevelopmentTools} data-id="element-77" />
          <PartnerPromotion data-id="element-78" />
          <Footer data-id="element-79" />
        </div>
      </div>
    </div>;
};
export default MainContent;